import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import {
  adminCreateUser, adminDeleteUser, adminGrantMpCode,
  adminListGrants, adminListProducts, adminListUsers,
  adminResetPassword, adminRevokeMpCode, adminUpdateUser,
  api,
} from "@/lib/api";
import type { AdminUser, MpInfo } from "@/types/api";
import { useAppSelector } from "@/store";
import { selectIsAdmin, selectUserId } from "@/store/authSlice";

interface PwForm {
  password: string;
  password_confirm: string;
}

export function UsersPage() {
  const userId = useAppSelector(selectUserId);
  const isAdmin = useAppSelector(selectIsAdmin);

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-semibold">Account</h1>

      <section>
        <h2 className="text-lg font-medium mb-3">My profile</h2>
        <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm">
          <div>
            <span className="text-slate-500">user_id:</span>{" "}
            <code className="font-mono">{userId}</code>
            {isAdmin && (
              <span className="ml-2 rounded bg-brand/10 text-brand text-xs font-medium px-1.5 py-0.5">
                admin
              </span>
            )}
          </div>
        </div>
      </section>

      <SelfPasswordChange userId={userId} />

      {isAdmin && <AdminUserSection callerUserId={userId} />}
    </div>
  );
}

// ============================================================ self password
function SelfPasswordChange({ userId }: { userId: string | null }) {
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);
  const { register, handleSubmit, reset, formState: { errors } } = useForm<PwForm>();

  const onSubmit = async (form: PwForm) => {
    if (form.password !== form.password_confirm) {
      setStatus({ kind: "err", msg: "Passwords do not match." }); return;
    }
    setSubmitting(true); setStatus(null);
    try {
      await api.post("/api/operation/change_password", {
        user_id: userId, password: form.password,
      });
      setStatus({ kind: "ok", msg: "Password changed." });
      reset();
    } catch (e: unknown) {
      setStatus({ kind: "err", msg: extractDetail(e, "Failed to change password.") });
    } finally { setSubmitting(false); }
  };

  return (
    <section>
      <h2 className="text-lg font-medium mb-3">Change password</h2>
      <form onSubmit={handleSubmit(onSubmit)} className="max-w-sm space-y-3 rounded-lg border border-slate-200 bg-white p-4">
        <PasswordField label="New password" {...register("password", { required: "Required", minLength: { value: 6, message: "At least 6 characters" } })} error={errors.password?.message} />
        <PasswordField label="Confirm" {...register("password_confirm", { required: "Required" })} error={errors.password_confirm?.message} />
        {status && <StatusBox kind={status.kind} msg={status.msg} />}
        <PrimaryButton submitting={submitting}>{submitting ? "Saving…" : "Change password"}</PrimaryButton>
      </form>
    </section>
  );
}

// ============================================================ admin section
function AdminUserSection({ callerUserId }: { callerUserId: string | null }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [products, setProducts] = useState<MpInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const [u, p] = await Promise.all([adminListUsers(), adminListProducts()]);
        setUsers(u); setProducts(p); setError(null);
      } catch (e: unknown) { setError(extractDetail(e, "Failed to load admin data.")); }
    })();
  }, [reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  return (
    <section className="border-t border-slate-200 pt-8 mt-8">
      <h2 className="text-xl font-semibold mb-4">Admin · Users & Grants</h2>
      {error && <StatusBox kind="err" msg={error} />}

      <CreateUserForm onCreated={reload} />

      {users && (
        <div className="mt-6 space-y-3">
          {users.map((u) => (
            <UserRow
              key={u.user_id}
              user={u}
              isSelf={u.user_id === callerUserId}
              products={products ?? []}
              onChange={reload}
            />
          ))}
        </div>
      )}
    </section>
  );
}

interface CreateUserForm {
  user_id: string;
  password: string;
  display_name: string;
  is_admin: boolean;
}

function CreateUserForm({ onCreated }: { onCreated: () => void }) {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const { register, handleSubmit, reset } = useForm<CreateUserForm>({
    defaultValues: { is_admin: false },
  });

  const submit = async (f: CreateUserForm) => {
    setBusy(true); setStatus(null);
    try {
      await adminCreateUser({
        user_id: f.user_id,
        password: f.password,
        display_name: f.display_name || undefined,
        is_admin: f.is_admin,
      });
      reset(); onCreated();
    } catch (e: unknown) {
      setStatus(extractDetail(e, "Failed to create user."));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="rounded-lg border border-slate-200 bg-white p-4">
      <h3 className="text-sm font-medium text-slate-700 mb-3">Create operator</h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <input {...register("user_id", { required: true })} placeholder="user_id" className={inputCls} />
        <input {...register("password", { required: true })} type="password" placeholder="password" className={inputCls} />
        <input {...register("display_name")} placeholder="display name (optional)" className={inputCls} />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" {...register("is_admin")} />
          <span>admin</span>
        </label>
      </div>
      {status && <p className="mt-2 text-xs text-red-600">{status}</p>}
      <div className="mt-3"><PrimaryButton submitting={busy}>{busy ? "Creating…" : "Create"}</PrimaryButton></div>
    </form>
  );
}

function UserRow({ user, isSelf, products, onChange }: {
  user: AdminUser;
  isSelf: boolean;
  products: MpInfo[];
  onChange: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [grants, setGrants] = useState<string[] | null>(null);
  const [pendingMp, setPendingMp] = useState<string>("");
  const [resetPw, setResetPw] = useState<string>("");
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!expanded) return;
    adminListGrants(user.user_id).then(setGrants).catch((e: unknown) =>
      setMsg(extractDetail(e, "Failed to load grants.")),
    );
  }, [expanded, user.user_id]);

  const togglePatch = async (patch: { is_active?: boolean; is_admin?: boolean }) => {
    setMsg(null);
    try { await adminUpdateUser(user.user_id, patch); onChange(); }
    catch (e: unknown) { setMsg(extractDetail(e, "Update failed.")); }
  };

  const grant = async () => {
    if (!pendingMp) return;
    setMsg(null);
    try {
      await adminGrantMpCode(user.user_id, pendingMp);
      setGrants(await adminListGrants(user.user_id));
      setPendingMp("");
    } catch (e: unknown) { setMsg(extractDetail(e, "Grant failed.")); }
  };

  const revoke = async (mp: string) => {
    setMsg(null);
    try {
      await adminRevokeMpCode(user.user_id, mp);
      setGrants(await adminListGrants(user.user_id));
    } catch (e: unknown) { setMsg(extractDetail(e, "Revoke failed.")); }
  };

  const remove = async () => {
    if (!confirm(`Delete user ${user.user_id}? Grants will be wiped too.`)) return;
    setMsg(null);
    try { await adminDeleteUser(user.user_id); onChange(); }
    catch (e: unknown) { setMsg(extractDetail(e, "Delete failed.")); }
  };

  const reset = async () => {
    if (!resetPw) return;
    setMsg(null);
    try { await adminResetPassword(user.user_id, resetPw); setResetPw(""); setMsg("Password reset."); }
    catch (e: unknown) { setMsg(extractDetail(e, "Reset failed.")); }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3 text-sm">
          <code className="font-mono">{user.user_id}</code>
          <span className="text-slate-500">{user.display_name ?? "-"}</span>
          {user.is_admin && <span className="rounded bg-brand/10 text-brand text-xs px-1.5 py-0.5">admin</span>}
          {!user.is_active && <span className="rounded bg-slate-100 text-slate-500 text-xs px-1.5 py-0.5">inactive</span>}
          {isSelf && <span className="rounded bg-amber-50 text-amber-700 text-xs px-1.5 py-0.5">self</span>}
        </div>
        <div className="flex items-center gap-2 text-xs">
          <button onClick={() => togglePatch({ is_active: !user.is_active })} className={btnGhost}>
            {user.is_active ? "Deactivate" : "Activate"}
          </button>
          <button onClick={() => togglePatch({ is_admin: !user.is_admin })} className={btnGhost} disabled={isSelf} title={isSelf ? "Cannot change your own admin flag" : ""}>
            {user.is_admin ? "Demote" : "Promote"}
          </button>
          <button onClick={remove} className={`${btnGhost} text-red-600 disabled:text-slate-400`} disabled={isSelf} title={isSelf ? "Cannot delete yourself" : ""}>
            Delete
          </button>
          <button onClick={() => setExpanded((e) => !e)} className={btnGhost}>
            {expanded ? "Collapse" : "Manage…"}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-4 text-sm">
          {msg && <p className="text-xs text-slate-600">{msg}</p>}

          <div>
            <h4 className="font-medium text-slate-700 mb-2">Grants</h4>
            <div className="flex flex-wrap gap-2 mb-2">
              {grants === null && <span className="text-slate-400 text-xs">loading…</span>}
              {grants && grants.length === 0 && <span className="text-slate-400 text-xs italic">none</span>}
              {grants && grants.map((mp) => (
                <span key={mp} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs">
                  {mp}
                  <button onClick={() => revoke(mp)} className="text-red-600 hover:text-red-700">×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <select value={pendingMp} onChange={(e) => setPendingMp(e.target.value)} className={inputCls}>
                <option value="">Select mp_code…</option>
                {products
                  .filter((p) => !grants?.includes(p.mp_code))
                  .map((p) => <option key={p.mp_code} value={p.mp_code}>{p.mp_code} ({p.product_line})</option>)}
              </select>
              <button onClick={grant} className={btnGhost} disabled={!pendingMp}>Grant</button>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-slate-700 mb-2">Reset password</h4>
            <div className="flex gap-2">
              <input value={resetPw} onChange={(e) => setResetPw(e.target.value)} type="password" placeholder="new password" className={inputCls} />
              <button onClick={reset} className={btnGhost} disabled={!resetPw}>Reset</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================ ui helpers
const inputCls =
  "block w-full rounded-md border border-slate-300 px-2 py-1 text-sm focus:border-brand focus:ring-2 focus:ring-brand/30 outline-none";
const btnGhost =
  "rounded-md border border-slate-300 px-2.5 py-1 text-xs hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed";

function PasswordField({ label, error, ...rest }: { label: string; error?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      <input type="password" autoComplete="new-password" {...rest}
        className="mt-1 block w-full rounded-md border-slate-300 border px-3 py-2 focus:border-brand focus:ring-2 focus:ring-brand/40 outline-none" />
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function StatusBox({ kind, msg }: { kind: "ok" | "err"; msg: string }) {
  return (
    <div className={
      kind === "ok"
        ? "rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700"
        : "rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700"
    }>{msg}</div>
  );
}

function PrimaryButton({ submitting, children }: { submitting: boolean; children: React.ReactNode }) {
  return (
    <button type="submit" disabled={submitting}
      className="rounded-md bg-brand text-white px-4 py-2 font-medium hover:bg-brand-700 disabled:opacity-60 disabled:cursor-not-allowed">
      {children}
    </button>
  );
}

function extractDetail(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? fallback;
}
